import Anthropic from '@anthropic-ai/sdk';
import { UserError } from '../../errors';
import type { Model, ModelMetadata, ModelProvider } from '../../model';
import {
  DEFAULT_ANTHROPIC_TIMEOUT_MS,
  getDefaultAnthropicBaseURL,
  getDefaultAnthropicKey,
  getDefaultAnthropicModel,
} from './defaults';
import {
  type AnthropicClient,
  type AnthropicFetch,
  AnthropicMessagesModel,
  type AnthropicMessagesModelOptions,
} from './messages-model';

export type AnthropicModelMetadataResolver =
  | Record<string, ModelMetadata>
  | ((modelName: string) => ModelMetadata | undefined);

export type AnthropicProviderOptions = AnthropicMessagesModelOptions & {
  apiKey?: string;
  baseURL?: string;
  defaultModel?: string;
  maxRetries?: number;
  fetch?: AnthropicFetch;
  anthropicClient?: AnthropicClient;
  modelMetadata?: AnthropicModelMetadataResolver;
};

export class AnthropicProvider implements ModelProvider {
  #client?: AnthropicClient;
  #modelCache = new Map<string, Model>();
  #options: AnthropicProviderOptions;

  constructor(options: AnthropicProviderOptions = {}) {
    if (options.anthropicClient) {
      if (options.apiKey) {
        throw new Error('Cannot provide both apiKey and anthropicClient');
      }
      if (options.baseURL) {
        throw new Error('Cannot provide both baseURL and anthropicClient');
      }
      if (options.fetch) {
        throw new Error('Cannot provide both fetch and anthropicClient');
      }
      this.#client = options.anthropicClient;
    }
    this.#options = options;
  }

  getDefaultModelName(): string {
    return this.#options.defaultModel || getDefaultAnthropicModel();
  }

  getModelMetadata(modelName?: string): ModelMetadata | undefined {
    const resolvedModelName = modelName || this.getDefaultModelName();
    return (
      resolveConfiguredModelMetadata(
        this.#options.modelMetadata,
        resolvedModelName,
      ) ?? getKnownAnthropicModelMetadata(resolvedModelName)
    );
  }

  #getClient(): AnthropicClient {
    if (!this.#client) {
      const apiKey = this.#options.apiKey ?? getDefaultAnthropicKey();
      if (!apiKey) {
        throw new UserError(
          'Missing Anthropic API key. Pass apiKey to AnthropicProvider or set ANTHROPIC_API_KEY.',
        );
      }
      this.#client = new Anthropic({
        apiKey,
        baseURL: this.#options.baseURL ?? getDefaultAnthropicBaseURL(),
        fetch: this.#options.fetch,
        maxRetries: this.#options.maxRetries,
        timeout: DEFAULT_ANTHROPIC_TIMEOUT_MS,
      });
    }
    return this.#client;
  }

  async getModel(modelName?: string): Promise<Model> {
    const model = modelName || this.getDefaultModelName();
    const cacheKey = JSON.stringify([model, 'anthropic_messages']);

    const cachedModel = this.#modelCache.get(cacheKey);
    if (cachedModel) {
      return cachedModel;
    }

    const resolvedModel = new AnthropicMessagesModel({
      client: this.#getClient(),
      model,
      metadata: this.getModelMetadata(model),
      options: this.#options,
    });
    this.#modelCache.set(cacheKey, resolvedModel);
    return resolvedModel;
  }

  async close(): Promise<void> {
    const cachedModels = Array.from(new Set(this.#modelCache.values()));
    this.#modelCache.clear();

    const closeErrors: unknown[] = [];
    await Promise.all(
      cachedModels.map(async (model) => {
        const maybeClose = (model as { close?: () => Promise<void> | void })
          .close;
        if (typeof maybeClose !== 'function') {
          return;
        }
        try {
          await maybeClose.call(model);
        } catch (error) {
          closeErrors.push(error);
        }
      }),
    );

    if (closeErrors.length === 1) {
      throw closeErrors[0];
    }
    if (closeErrors.length > 1) {
      const error = new Error('Failed to close AnthropicProvider.');
      (error as Error & { causes?: unknown[] }).causes = closeErrors;
      throw error;
    }
  }
}

function resolveConfiguredModelMetadata(
  resolver: AnthropicModelMetadataResolver | undefined,
  modelName: string,
): ModelMetadata | undefined {
  if (!resolver) {
    return undefined;
  }
  if (typeof resolver === 'function') {
    return resolver(modelName);
  }
  return resolver[modelName] ?? resolver[modelName.toLowerCase()];
}

function getKnownAnthropicModelMetadata(
  modelName: string,
): ModelMetadata | undefined {
  const normalized = modelName.trim().toLowerCase();
  if (!normalized) {
    return undefined;
  }
  if (normalized.includes('claude-3-7-sonnet')) {
    return {
      contextWindowTokens: 200_000,
      maxOutputTokens: 64_000,
      multimodal: {
        imageInput: true,
        imageToolResult: 'native',
      },
    };
  }
  if (normalized.includes('claude-3-5') || normalized.includes('claude-3')) {
    return {
      contextWindowTokens: 200_000,
      maxOutputTokens: 8192,
      multimodal: {
        imageInput: true,
        imageToolResult: 'native',
      },
    };
  }
  return undefined;
}
